/** React libraries */
import { useContext, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { toast } from "react-toastify";

/** Custom components types, services */
import Docupload from "../../../../components/Upload/Upload";
import { RequestMetadaContext } from "./../../logoRequest.Type";
import { DocumentUploadConstants, DocumentUploadedData, documentUploadService, DownloadDocumentResponse } from "./../../documentUploadService";
import { useLoader } from "../../../../context/LoaderContext";

/**  Style */
import "./style.css";

const DocumentUpload = () => {
  const requestMetadaContext = useContext(RequestMetadaContext);
  const { setValue, watch, register, getValues } = useFormContext();
  const { setLoading } = useLoader();
  const [filesInfo, setFilesInfo] = useState<DocumentUploadedData>();
  const [isFolderPath, setIsFolderPath] = useState<boolean>(true);
  const [isDocSuccess, setIsDocSuccess] = useState<boolean>(false);
  const [isDocFail, setIsDocFail] = useState<boolean>(false);

  const folderPath = watch("customFolderPath");

  useEffect(() => {
    fetchUploadedFile()
  }, [])

  /**
   * Is file name safe
   * @param name 
   * @returns 
   */
  const isSafeFileName = (name: string) =>
  !name.includes('..') && !name.includes('/') && !name.includes('\\');

  /**
   * Is file valid
   * @param file 
   * @returns 
   */
  const isValidFile = (file: File): string | null => {
    const ext = file.name.split('.').pop()?.toLowerCase();

    if (!ext || !DocumentUploadConstants.DOCUMENT_ALLOWED_EXTENSIONS.includes(ext)) {
      return DocumentUploadConstants.FILE_TYPE_NOT_SUPPORT_MSG;
    }

    if (!DocumentUploadConstants.DOCUMENT_ALLOWED_MIME_TYPES.includes(file.type)) {
      return DocumentUploadConstants.FILE_TYPE_NOT_SUPPORT_MSG;
    }

    if (!isSafeFileName(file.name)) {
      return DocumentUploadConstants.INVALID_FILE_NAME;
    }

    return null;
  };

  /**
   * Fetch upload files
   */
  const fetchUploadedFile = async () => {
    try {
      let folderPath = getValues("customFolderPath");
      if (folderPath && folderPath != "") {
        await documentUploadService.fetchAllUploadedDocuments(folderPath)
          .then(response => {
            if (response) {
              setFilesInfo(response);
            } else {
              let documentUploadedData: DocumentUploadedData = { files: [] }
              setFilesInfo(documentUploadedData);
            }
          })
          .catch(error => {
            let documentUploadedData: DocumentUploadedData = { files: [] }
            setFilesInfo(documentUploadedData);
          })
      }
    } catch (exe) {
      let documentUploadedData: DocumentUploadedData = { files: [] }
      setFilesInfo(documentUploadedData);
    }
  };

  /**
   * On file upload
   * @param files 
   * @returns 
   */
  const onFileUpload = async (files: File[]) => {
    setLoading(true);
    try {
      if (!isFolderPath) {
        toast.error(DocumentUploadConstants.DOCUMET_FILEPATH_NOT_PRESENT);
        setLoading(false);
        return;
      }

      const folderPath = getValues("customFolderPath");
      if (!folderPath) {
        toast.error(DocumentUploadConstants.UPLOAD_DOCUMENTS_FAIL_MSG);
        setLoading(false);
        return;
      }

      let hasValidFile = false;

      for (const file of files) {
        const error = isValidFile(file);
        if (error) {
          toast.error(`${file.name}: ${error}`);
          setIsDocFail(true);
          setTimeout(() => setIsDocFail(false), 2000);
          continue;
        }

        hasValidFile = true;

        const timestamp = Date.now();
        const fName = file.name.split(".");
        const ext = file.name.split(".").pop();
        const newFileName = `${fName[0]}_${timestamp}.${ext}`;

        const renamedFile = new File([file], newFileName, {
          type: file.type,
          lastModified: file.lastModified,
        });

        try {
          const response = await documentUploadService.uploadFile(folderPath, renamedFile);
          setIsDocSuccess(true);
          setFilesInfo((prev) => ({
            files: prev?.files ? [...prev.files, response] : [response],
          }));
          setTimeout(() => setIsDocSuccess(false), 4000);
        } catch (err) {
          setIsDocFail(true);
          setTimeout(() => setIsDocFail(false), 4000);
        }
      }

      // If no valid file existed, hide loader
      if (!hasValidFile) {
        setLoading(false);
        return;
      }

    } catch (err) {
      toast.error(DocumentUploadConstants.UPLOAD_DOCUMENTS_FAIL_MSG);
    } finally {
      // ALWAYS hide loader when loop finishes
      setLoading(false);
    }
  };

  /**
   * On file download
   * @param filename 
   */
  const onFileDownload = async (filename: string) => {
    try {
      setLoading(true);
      let folderPath = getValues("customFolderPath");
      let filePath = folderPath + "/" + filename;
      let response: DownloadDocumentResponse = await documentUploadService.downloadDocument(filePath);
      if (response && response.fileBlob) {
        const url = window.URL.createObjectURL(response.fileBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      }
      setLoading(false);
    } catch (exe) {
      setLoading(false);
      toast.error(DocumentUploadConstants.DOWNLOAD_DOCUMENTS_FAIL_MSG);
    }
  }

  return (
    <>
      {requestMetadaContext.isDocumentUploadEnabled && (!folderPath || folderPath === "") ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '390px' }}>
          Loading...
        </div>
      ) : (
        <Docupload
          filesInfo={filesInfo}
          visible={false}
          disabled={!requestMetadaContext.isDocumentUploadEnabled}
          sidebyside={false}
          isFolderPath={isFolderPath}
          isDocSuccess={isDocSuccess}
          isDocFail={isDocFail}
          supportedFileTypes={DocumentUploadConstants.DOCUMENT_ALLOWED_MIME_TYPES}
          infoMessage="Please attach a proof file showing the FSC label as it will appear in the final publication, along with any other FSC references (e.g., in the document text) in a PDF file"
          onFileUpload={onFileUpload}
          onFileDownload={onFileDownload}
        />
      )}
    </>
  )
}

export default DocumentUpload